import { useEffect, useState } from "react";
import * as React from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Typography from "@mui/material/Typography";
import {
  AppButton,
  AppDialog,
  AppTable,
  AppTableCell,
  AppTableHeadCell,
  AppTableRow,
  FilterBar,
  FormField,
  FormSection,
  PageHeader,
  RowNumberCell,
  toast,
} from "../../components/ui";

import {
  ClioService,
  EntryType,
  MatterPreviewResponse,
  RingCentralService,
  TaskPreviewResponse,
  ZoomCommunicationViewModel,
  ZoomService,
  ZoomTableRequest,
} from "../../api";
import { RingCentralExtensionResponse } from "../../api/models/RingCentralExtensionRsponse";
import "./ZoomPhone.scss";
import {
  Box,
  Checkbox,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  LeadsSelectAllHeadCell,
  mapLeadHeadCells,
} from "../Admin/LeadsManagement/LeadsTables/LeadTableHeader";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  DEFAULT_ROWS_PER_PAGE,
  HeadCell,
  TableInteractionsProps,
} from "../Admin/LeadsManagement/LeadsTables/LeadsTableConstants";
import { useDispatch } from "react-redux";
import { startSpinner, stopSpinner } from "../../redux/actions/spinnerActions";
import { ClioMatterAutocomplete } from "../../components/shared/MatterAutocomplete";

dayjs.extend(utc);
dayjs.extend(timezone);

const PHONE_TABLE_HEAD: HeadCell[] = [
  { id: "path", disablePadding: false, label: "Type" },
  { id: "matterName", disablePadding: false, label: "Matter name" },
  { id: "clientPhone", disablePadding: false, label: "Callee phone" },
  { id: "clientName", disablePadding: false, label: "Callee name" },
  { id: "message", disablePadding: false, label: "Text message" },
  { id: "duration", disablePadding: false, label: "Duration" },
  { id: "created", disablePadding: false, label: "Created at" },
];

const MySwal = withReactContent(Swal);

export interface PhoneContact {
  phoneNumber: string;
  name: string;
}

export default function RingCentralPhone() {
  const rcExtensionKey = "RC_EXTENSION_ID";

  const [callMatterValue, setCallMatterValue] =
    React.useState<MatterPreviewResponse | null>(null);
  const [callContactOptions, setCallContactOptions] = React.useState<
    readonly PhoneContact[]
  >([]);
  const [callContactValue, setCallContactValue] =
    React.useState<PhoneContact | null>(null);
  const [callInProgress, setCallInProgress] = React.useState(false);

  const callContactValueRef = React.useRef(callContactValue);
  const callMatterValueRef = React.useRef(callMatterValue);

  const [matterValue, setMatterValue] =
    React.useState<MatterPreviewResponse | null>(null);
  const [taskValue, setTaskValue] = React.useState<TaskPreviewResponse | null>(
    null
  );
  const [taskOptions, setTaskOptions] = React.useState<
    readonly TaskPreviewResponse[]
  >([]);
  const [notesValue, setNotesValue] = React.useState<string | null>(null);

  const [rcUsers, setRcUsers] = React.useState<RingCentralExtensionResponse[]>([]);
  const [selectedExtensionId, setSelectedExtensionId] = React.useState("");

  const [zoomCommunications, setZoomCommunications] = React.useState<
    ZoomCommunicationViewModel[]
  >([]);
  const [selectedComm, setSelectedComm] =
    React.useState<ZoomCommunicationViewModel | null>(null);

  const [selectUserDialogOpen, setSelectUserDialogOpen] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [selectedCalls, setSelectedCalls] = useState<number[]>([]);
  const [assignCallDialogOpen, setAssignCallDialogOpen] = React.useState(false);

  const [tableInteractions, setTableInteractions] =
    useState<TableInteractionsProps>({
      sortAsc: false,
      sortBy: "created",
      filterValue: null,
      page: 0,
      rowsPerPage: DEFAULT_ROWS_PER_PAGE,
    });
  const [totalCount, setTotalCount] = useState(0);
  const [bulkClicked, setBulkClicked] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    loadRingCentralCommunications();
  }, [tableInteractions]);

  const handleCommunicationClick = (index: number) => {
    setSelectedCalls([]);
    setBulkClicked(false);
    const selected = zoomCommunications[index];
    setSelectedComm(selected);
    if (selected.matterId) {
      setMatterValue({
        id: selected.matterId,
        description: selected.matterName,
      });
      loadTasks(selected.matterId);
    } else {
      setMatterValue(null);
    }

    setTaskOptions([]);
    setTaskValue(null);
    setNotesValue(
      selected.type == EntryType.TEXT ? (selected.message ?? "") : ""
    );
    setAssignCallDialogOpen(true);
  };

  const handleBulkSave = () => {
    const comms = zoomCommunications.filter((c) =>
      selectedCalls.includes(c.id!)
    );
    setSelectedComm(comms[0]);
    const matter = comms.filter((c) => !!c.matterId)[0];

    if (matter?.matterId) {
      setMatterValue({
        id: matter.matterId,
        description: matter.matterName,
      });
      loadTasks(matter.matterId);
    } else {
      setMatterValue(null);
    }

    setTaskOptions([]);
    setTaskValue(null);

    const combinedMessages = comms
      .filter((c) => !!c.message)
      .sort((a, b) => (a.id ?? 0) - (b.id ?? 0))
      .map((c) => c.message)
      .join("\n \n");
    setNotesValue(combinedMessages);
    setAssignCallDialogOpen(true);
    setBulkClicked(true);
  };

  const loadRingCentralUsers = async () => {
    const response = await RingCentralService.getApiRingCentralUsers();
    setRcUsers(response);
    setSelectUserDialogOpen(true);
  };

  const handleUserSelected = async () => {
    const user = rcUsers.find((f) => f.id === selectedExtensionId);
    if (!user?.id) {
      return;
    }
    localStorage.setItem(rcExtensionKey, user.id);
    setSelectUserDialogOpen(false);
    await loadRingCentralCommunications();
  };

  async function loadRingCentralCommunications() {
    const extensionId = localStorage.getItem(rcExtensionKey);

    if (!extensionId) {
      await loadRingCentralUsers();
      return;
    }
    dispatch(startSpinner());
    try {
      const response = await RingCentralService.getApiRingCentralCallLogs({
        extensionId,
      });
      const mapped: ZoomCommunicationViewModel[] = (response ?? []).map((log, index) => ({
        id: index + 1,
        callId: log.id ?? undefined,
        clientPhone: log.to ?? log.from ?? undefined,
        clientName: log.calleeName ?? log.callerName ?? undefined,
        duration: log.duration ?? undefined,
        created: log.startTime ?? undefined,
        message: log.direction ?? undefined,
      }));
      setZoomCommunications(mapped);
      setTotalCount(mapped.length);
    } catch {
      setZoomCommunications([]);
      setTotalCount(0);
      toast.error("Could not load RingCentral call logs.");
    } finally {
      dispatch(stopSpinner());
    }
  }

  async function loadTasks(matterId: number) {
    if (!matterId) {
      setTaskOptions([]);
      setTaskValue(null);
      setNotesValue("");
      return;
    }

    let responseOptions = await ClioService.getApiClioMattersTasks(matterId);
    responseOptions.push({ name: "Other", id: undefined });
    responseOptions = [
      { name: "Call with client", id: 0 },
      { name: "Call with OC", id: 1 },
      { name: "Text with client", id: 2 },
      { name: "Text with OC", id: 3 },
      { name: "Internal call", id: 4 },
      { name: "Internal text", id: 5 },
      ...responseOptions,
    ];
    setTaskOptions(responseOptions);
  }

  async function loadContacts(matterId: number) {
    if (!matterId) {
      setCallContactOptions([]);
      setCallContactValue(null);
      return;
    }

    const responseOptions = await ClioService.getApiClioMattersContacts(
      matterId
    );
    let contacts: PhoneContact[] = [];
    responseOptions.forEach((contact) => {
      const contactPhones = contact.phone_numbers?.map((m) => {
        return {
          name: `${contact.relationship_name}: ${contact.name}`,
          phoneNumber: normalizePhone(m.number),
        };
      });

      contacts = contacts.concat(contactPhones as PhoneContact[]);
    });

    setCallContactOptions(contacts);
  }

  function normalizePhone(phoneNumber: string | null | undefined) {
    if (!phoneNumber) {
      return "";
    }

    if (!phoneNumber.startsWith("+")) {
      phoneNumber = `+1${phoneNumber}`;
    }

    return phoneNumber
      .replace("-", "")
      .replace("-", "")
      .replace("(", "")
      .replace(")", "");
  }

  useEffect(() => {
    callContactValueRef.current = callContactValue;
  }, [callContactValue]);

  useEffect(() => {
    callMatterValueRef.current = callMatterValue;
  }, [callMatterValue]);

  useEffect(() => {
    window.addEventListener("message", handleZoomEvents);
    loadRingCentralCommunications();
    return function cleanup() {
      window.removeEventListener("message", handleZoomEvents);
    };
  }, []);

  const handleZoomEvents = async (e) => {
    const data = e.data;
    if (data) {
      switch (data.type) {
        case "zp-call-ringing-event": {
          setCallInProgress(true);
          break;
        }
        case "zp-call-connected-event": {
          if (callContactValueRef.current) {
            setTimeout(async () => {
              await ZoomService.postApiZoomMatter({
                callId: data.data.callId,
                matterId: callMatterValueRef.current?.id,
                matterName: callMatterValueRef.current?.description,
                clientName: callContactValueRef.current!.name,
              });
            }, 3000);
          }
          setCallInProgress(true);
          break;
        }
        case "zp-call-log-completed-event": {
          setTimeout(async () => {
            if (callContactValueRef.current) {
              await ZoomService.postApiZoomMatter({
                callId: data.data.callId,
                matterId: callMatterValueRef.current?.id,
                matterName: callMatterValueRef.current?.description,
                clientName: callContactValueRef.current!.name,
              });
            }
            setCallMatterValue(null);
            setCallContactOptions([]);
            setCallContactValue(null);
            setCallInProgress(false);

            await loadRingCentralCommunications();
          }, 3000);
          break;
        }
        case "zp-call-voicemail-received-event":
        case "zp-sms-log-event":
          setCallInProgress(false);
          setCallMatterValue(null);
          setCallContactOptions([]);
          setCallContactValue(null);
          setTimeout(async () => {
            await loadRingCentralCommunications();
          }, 3000);
          break;
        default: {
          break;
        }
      }
    }
  };

  function handleCall() {
    const phoneNumber = callContactValue?.phoneNumber;
    const zoomUrl = `zoomphonecall://${phoneNumber}`;
    window.location.href = zoomUrl;
  }

  async function onSubmit() {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    toast.info("Creating Clio record!");

    if (bulkClicked && !!selectedCalls.length) {
      setBulkClicked(false);
      ZoomService.postApiZoomBulk({
        bulkCommIds: selectedCalls,
        callId: selectedComm?.callId,
        matterId: matterValue?.id,
        taskId: taskValue?.id,
        taskName: taskValue?.name,
        notes: notesValue,
      })
        .then(() => {
          const newComms = zoomCommunications.filter(
            (f) => !selectedCalls.includes(+f.id!)
          );
          setZoomCommunications(newComms);
          toast.success("Communications assigned.");
        })
        .catch(() => {
          toast.error("Could not assign communications.");
        })
        .finally(() => {
          setSelectedComm(null);
          setMatterValue(null);
          setTaskValue(null);
          setSelectedCalls([]);
          setNotesValue("");
          setTaskOptions([]);
          setIsSubmitting(false);
          setAssignCallDialogOpen(false);
        });
    } else {
      ZoomService.postApiZoom({
        commId: selectedComm?.id ?? undefined,
        callId: selectedComm?.callId,
        matterId: matterValue?.id,
        taskId: taskValue?.id,
        taskName: taskValue?.name,
        notes: notesValue,
      })
        .then(() => {
          const newComms = zoomCommunications.filter(
            (f) => f.callId !== selectedComm?.callId
          );
          setZoomCommunications(newComms);
          toast.success("Communication assigned.");
        })
        .catch(() => {
          toast.error("Could not assign communication.");
        })
        .finally(() => {
          setSelectedComm(null);
          setMatterValue(null);
          setTaskValue(null);
          setNotesValue("");
          setTaskOptions([]);
          setIsSubmitting(false);
          setAssignCallDialogOpen(false);
        });
    }
  }

  const handleSelectAllClick = () => {
    if (zoomCommunications.length === selectedCalls.length) {
      setSelectedCalls([]);
      return;
    }
    const newSelected = zoomCommunications.map((n) => n.id!);
    setSelectedCalls(newSelected);
  };

  const handleDeleteTableComm = async (id, e: any = null) => {
    if (e != null) {
      e.preventDefault();
      e.stopPropagation();
    }
    MySwal.fire({
      title: <strong>Confirm communication deletion</strong>,
      icon: "info",
      showCancelButton: true,
      cancelButtonText: "No, keep it",
      confirmButtonText: "Yes, remove",
      reverseButtons: true,
    }).then(async (result) => {
      if (result.isConfirmed) {
        await ZoomService.postApiZoomStatusDeleted(id);
        const newComms = zoomCommunications.filter((f) => f.id !== id);
        setZoomCommunications(newComms);
        toast.success("Communication removed.");
      }
    });
  };

  const handleBulkDelete = () => {
    selectedCalls.forEach((id) => handleDeleteTableComm(id));
  };

  const handleRowClick = (e, id: number) => {
    e.preventDefault();
    e.stopPropagation();
    const selectedIndex = selectedCalls.indexOf(id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = selectedCalls.concat(id);
    } else if (selectedIndex === 0) {
      newSelected = selectedCalls.slice(1);
    } else if (selectedIndex === selectedCalls.length - 1) {
      newSelected = selectedCalls.slice(0, -1);
    } else if (selectedIndex > 0) {
      newSelected = selectedCalls
        .slice(0, selectedIndex)
        .concat(selectedCalls.slice(selectedIndex + 1));
    }
    setSelectedCalls(newSelected);
  };

  const generateTableRow = (row: ZoomCommunicationViewModel, index) => {
    const isItemSelected = selectedCalls.indexOf(row.id!) !== -1;
    const labelId = `enhanced-table-checkbox-${index}`;
    return (
      <AppTableRow
        onClick={() => handleCommunicationClick(index)}
        role="checkbox"
        aria-checked={isItemSelected}
        tabIndex={-1}
        key={row.id}
        selected={isItemSelected}
        className={isItemSelected ? "table-row-accent" : ""}
        sx={{ cursor: "pointer" }}
      >
        <AppTableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            color="primary"
            checked={isItemSelected}
            onClick={(e) => handleRowClick(e, row.id!)}
            inputProps={{
              "aria-labelledby": labelId,
            }}
          />
        </AppTableCell>
        <RowNumberCell
          value={
            tableInteractions.page * tableInteractions.rowsPerPage + index + 1
          }
        />
        <AppTableCell align="left">
          {row.path} {row.type}
        </AppTableCell>
        <AppTableCell align="left">{row.matterName}</AppTableCell>
        <AppTableCell align="left">{row.clientPhone}</AppTableCell>
        <AppTableCell align="left">{row.clientName}</AppTableCell>
        <AppTableCell align="left" className="limit-rows">
          {row.message}
        </AppTableCell>
        <AppTableCell align="left">{row.duration}</AppTableCell>
        <AppTableCell align="left">
          {dayjs
            .utc(row.created)
            .local()
            .format("MM/DD/YYYY h:mmA")
            .toString()}
        </AppTableCell>
        <AppTableCell
          align="right"
          onClick={(e) => e.stopPropagation()}
        >
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={(e) => handleDeleteTableComm(row.id, e)}
              aria-label="Delete communication"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </AppTableCell>
      </AppTableRow>
    );
  };

  const handleRequestSort = (
    _event: React.MouseEvent<unknown>,
    newSortBy: keyof ZoomCommunicationViewModel
  ) => {
    const toggledOrder =
      tableInteractions.sortBy !== newSortBy
        ? true
        : !tableInteractions.sortAsc;
    setTableInteractions({
      ...tableInteractions,
      sortAsc: toggledOrder,
      sortBy: newSortBy,
    });
  };

  const handleFilterChange = (newFilter: string) => {
    setTableInteractions({
      ...tableInteractions,
      filterValue: newFilter,
      page: 0,
    });
  };

  const handleChangePage = (newPage: number) => {
    setTableInteractions({ ...tableInteractions, page: newPage });
  };

  const handleChangeRowsPerPage = (rowsPerPage: number) => {
    setTableInteractions({
      ...tableInteractions,
      rowsPerPage,
      page: 0,
    });
  };

  return (
    <Box className="zoom-container" sx={{ width: "100%", pb: 3 }}>
      <PageHeader
        title="RingCentral calls"
        subtitle="Review recent RingCentral call logs for a selected user."
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(320px, 360px) minmax(0, 1fr)",
          },
          gap: 2.5,
          alignItems: "start",
        }}
      >
        <Stack spacing={2}>
          <FormSection
            title="RingCentral user"
            subtitle="Select which RingCentral extension’s call logs to review."
          >
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                Outbound calling is done in the RingCentral app. This page loads
                recent call logs for the selected user.
              </Typography>
              <AppButton
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => void loadRingCentralUsers()}
              >
                {localStorage.getItem(rcExtensionKey)
                  ? "Change RingCentral user"
                  : "Select RingCentral user"}
              </AppButton>
            </Stack>
          </FormSection>
        </Stack>

        <Stack spacing={2} sx={{ minWidth: 0 }}>
          <FilterBar
            hideFilterButton
            hideClearButton
            leading={
              <TextField
                size="small"
                placeholder="Search in logs…"
                value={tableInteractions.filterValue ?? ""}
                onChange={(e) => handleFilterChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: { xs: "100%", sm: 260 },
                  bgcolor: "background.paper",
                  borderRadius: 1,
                }}
              />
            }
            trailing={
              <>
                <Tooltip title="Only texts could be sent together" arrow>
                  <span>
                    <AppButton
                      disabled={selectedCalls.length === 0}
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={handleBulkSave}
                    >
                      Save selected
                    </AppButton>
                  </span>
                </Tooltip>
                <AppButton
                  disabled={selectedCalls.length === 0}
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={handleBulkDelete}
                >
                  Delete selected
                </AppButton>
              </>
            }
          />

          <AppTable
            columns={mapLeadHeadCells(PHONE_TABLE_HEAD)}
            showRowNumber
            size="medium"
            leadingHeadCell={
              <LeadsSelectAllHeadCell
                numSelected={selectedCalls.length}
                rowCount={zoomCommunications.length}
                onSelectAllClick={handleSelectAllClick}
              />
            }
            trailingHeadCells={
              <AppTableHeadCell align="right" sx={{ width: 56 }}>
                Actions
              </AppTableHeadCell>
            }
            sortBy={tableInteractions.sortBy}
            sortAsc={tableInteractions.sortAsc}
            onRequestSort={(columnId) =>
              handleRequestSort(
                {} as React.MouseEvent<unknown>,
                columnId as keyof ZoomCommunicationViewModel
              )
            }
            extraColumnCount={2}
            isEmpty={zoomCommunications.length === 0}
            emptyTitle="No communications"
            pagination={{
              count: totalCount,
              page: tableInteractions.page,
              rowsPerPage: tableInteractions.rowsPerPage,
              rowsPerPageOptions: [10, 25, 50, 100],
              onPageChange: handleChangePage,
              onRowsPerPageChange: handleChangeRowsPerPage,
            }}
          >
            {zoomCommunications.map((row, index) =>
              generateTableRow(row, index)
            )}
          </AppTable>
        </Stack>
      </Box>

      <AppDialog
        open={selectUserDialogOpen}
        title="Select your RingCentral user"
        actions={
          <AppButton
            variant="contained"
            color="primary"
            onClick={handleUserSelected}
            disabled={!selectedExtensionId}
            autoFocus
          >
            Select user
          </AppButton>
        }
      >
        <FormField
          select
          label="RingCentral user"
          value={selectedExtensionId}
          onChange={(e) => setSelectedExtensionId(String(e.target.value))}
        >
          {rcUsers.map((m) => (
            <MenuItem key={m.id as string} value={m.id as string}>
              {m.name}
              {m.extensionNumber ? ` (ext ${m.extensionNumber})` : ""}
            </MenuItem>
          ))}
        </FormField>
      </AppDialog>

      <AppDialog
        open={assignCallDialogOpen}
        onClose={() => setAssignCallDialogOpen(false)}
        maxWidth="sm"
        title="Assign call"
        actions={
          <>
            <AppButton
              variant="outlined"
              color="inherit"
              onClick={() => setAssignCallDialogOpen(false)}
            >
              Cancel
            </AppButton>
            <AppButton
              disabled={!matterValue || !taskValue || isSubmitting}
              variant="contained"
              color="primary"
              onClick={onSubmit}
            >
              Save
            </AppButton>
          </>
        }
      >
        {selectedComm !== null && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <ClioMatterAutocomplete
              value={matterValue}
              noOptionsText="No matters"
              onChange={(selectedValue) => {
                setMatterValue(selectedValue);
                loadTasks(selectedValue?.id as number);
              }}
            />

            <Autocomplete
              disabled={!matterValue}
              options={taskOptions}
              value={taskValue}
              onChange={(
                _event: any,
                selectedValue: TaskPreviewResponse | null
              ) => {
                setTaskValue(selectedValue);
              }}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : (option.name as string)
              }
              renderInput={(params) => (
                <TextField {...params} label="Choose a task" fullWidth />
              )}
            />

            <TextField
              value={notesValue ?? ""}
              onChange={(e) => setNotesValue(e.target.value)}
              disabled={!matterValue}
              label="Notes"
              multiline
              rows={4}
              fullWidth
            />
          </Stack>
        )}
      </AppDialog>
    </Box>
  );
}
